// Small presentational pieces: page header, empty state, status badge,
// error message (with the 402 upgrade prompt), loading block.

import type { ReactNode } from 'react';
import { errorInfo } from '../../lib/api';
import { Spinner } from './Button';

export function PageHeader({ title, description, actions }: { title: string; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-text">{title}</h1>
        {description && <div className="mt-1 text-sm text-muted">{description}</div>}
      </div>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-surface px-6 py-12 text-center">
      <h2 className="text-base font-semibold text-text">{title}</h2>
      {body && <p className="mx-auto mt-1 max-w-md text-sm text-muted">{body}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

const TONES = {
  neutral: 'bg-surface-2 text-muted',
  accent: 'bg-accent-soft text-accent',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
} as const;

export type Tone = keyof typeof TONES;

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${TONES[tone]}`}>
      {children}
    </span>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 py-8 text-sm text-muted">
      <Spinner label={label} />
      <span>{label}</span>
    </div>
  );
}

/**
 * Displays an error from api()/mutations. A 402 becomes an upgrade prompt
 * that names the feature instead of a generic failure.
 */
export function ErrorMessage({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const info = errorInfo(error);
  if (info.status === 402 && info.code === 'plan_limit_reached') {
    const unit = QUOTA_UNITS[info.feature ?? ''];
    return (
      <div role="alert" className="rounded-lg border border-accent bg-accent-soft p-4 text-sm">
        <p className="font-medium text-text">You’ve reached your plan’s limit</p>
        <p className="mt-1 text-muted">
          {unit && info.limit !== undefined
            ? `Your plan includes ${info.limit} ${info.limit === 1 ? unit[0] : unit[1]}.`
            : info.message}{' '}
          Upgrade on your site’s account page for more.
        </p>
      </div>
    );
  }
  if (info.status === 402) {
    return (
      <div role="alert" className="rounded-lg border border-accent bg-accent-soft p-4 text-sm">
        <p className="font-medium text-text">Upgrade to unlock this</p>
        <p className="mt-1 text-muted">
          {info.feature ? `“${featureLabel(info.feature)}” is included in a higher plan.` : info.message} Plans
          are managed on your site’s account page.
        </p>
      </div>
    );
  }
  return (
    <div role="alert" className="flex items-center justify-between gap-4 rounded-lg bg-danger-soft p-4 text-sm text-danger">
      <span>{info.message}</span>
      {onRetry && (
        <button type="button" onClick={onRetry} className="font-medium underline">
          Retry
        </button>
      )}
    </div>
  );
}

const QUOTA_UNITS: Record<string, [string, string]> = {
  podcasts: ['show', 'shows'],
  episodes_per_month: ['new episode per month', 'new episodes per month'],
  ai_transcription_minutes: ['minute of transcription per month', 'minutes of transcription per month'],
};

const FEATURE_LABELS: Record<string, string> = {
  ai_show_notes: 'AI show notes, chapters and highlights',
  ai_artwork: 'AI cover art',
  video_export: 'Video export',
  sponsorship_engine: 'Sponsorship engine',
  hermes_simulator: 'Sponsorship simulator',
};

export function featureLabel(key: string): string {
  return FEATURE_LABELS[key] ?? key.replace(/_/g, ' ');
}
